import { Fragment, useState } from "react";
import { Link, useLocation } from "react-router";
import { classes } from "../../nucleo/classes";
import { itemAtivo, itensDoMenu } from "../../nucleo/menu";
import { useMinhaEsteira } from "../../nucleo/implantacoes";
import { useAcessoAosChamados } from "../../nucleo/repasse";
import { podeNaSessao, useSessao } from "../../nucleo/sessao";

export interface PropsDoMenuLateral {
  lado: "cliente" | "interna";
}

/**
 * Menu fixo à esquerda (spec 4.1). Em tela estreita vira uma faixa acima do conteúdo — mesma
 * lista, sem gaveta escondida atrás de ícone.
 */
export function MenuLateral({ lado }: PropsDoMenuLateral) {
  const [implantacaoAberta, setImplantacaoAberta] = useState(false);
  const [estavaDentroDaImplantacao, setEstavaDentroDaImplantacao] = useState(false);
  const sessao = useSessao();
  // Sem `esteira:ver` (o acesso de consulta) não há o que perguntar: o servidor recusaria.
  const esteira = useMinhaEsteira(lado === "cliente" && podeNaSessao(sessao.data, "esteira:ver"));
  /* Só o cliente que vê chamados pergunta: a equipe nunca é bloqueada e quem não tem
     `ticket:ver` nem tem o item. O servidor responde a mesma coisa para os dois lados. */
  const acessoAosChamados = useAcessoAosChamados(
    lado === "cliente" && podeNaSessao(sessao.data, "ticket:ver"),
  );
  const { pathname } = useLocation();
  const implantacaoConcluida = Boolean(
    esteira.data && esteira.data.total > 0 && esteira.data.concluidas === esteira.data.total,
  );
  const itens = itensDoMenu(
    lado,
    sessao.data,
    implantacaoConcluida,
    esteira.data?.modulos ?? [],
    acessoAosChamados.data?.liberado === false,
  );
  // Quem chega direto num módulo ou na Agenda vê o grupo aberto, com a página atual marcada.
  const dentroDaImplantacao = itens.some((item) => item.subitem && itemAtivo(item, pathname, itens));
  if (dentroDaImplantacao !== estavaDentroDaImplantacao) {
    setEstavaDentroDaImplantacao(dentroDaImplantacao);
    if (dentroDaImplantacao) setImplantacaoAberta(true);
  }
  if (!sessao.data || itens.length === 0) return null;

  return (
    <nav className={classes("menu-lateral", lado === "cliente" && "menu-lateral--cliente")} aria-label="Menu principal" data-testid="menu-lateral">
      <ul className="menu-lateral__lista">
        {itens.map((item, indice) => {
          if (lado === "cliente" && item.subitem && !implantacaoAberta) return null;
          if (lado === "cliente" && item.rotulo === "Implantação" && item.para === "/app") {
            return <Fragment key="implantacao">
              <li className="menu-lateral__item">
                <button type="button" className="menu-lateral__link menu-lateral__alternar"
                  aria-expanded={implantacaoAberta} onClick={() => setImplantacaoAberta((aberta) => !aberta)}>
                  Implantação <span aria-hidden="true">{implantacaoAberta ? "▴" : "▾"}</span>
                </button>
              </li>
              {implantacaoAberta ? <li className="menu-lateral__item menu-lateral__item--sub">
                <Link
                  to="/app"
                  className={classes("menu-lateral__link", pathname === "/app" && "menu-lateral__link--ativo")}
                  {...(pathname === "/app" ? { "aria-current": "page" as const } : {})}
                >
                  Andamento
                </Link>
              </li> : null}
            </Fragment>;
          }
          const cabecalho = item.bloco && item.bloco !== itens[indice - 1]?.bloco ? (
            <li className="menu-lateral__grupo" aria-hidden="true">{item.bloco}</li>
          ) : null;
          if (!item.para) {
            return (
              <Fragment key={item.rotulo}>
              {cabecalho}
              <li className="menu-lateral__item">
                <span
                  className="menu-lateral__link menu-lateral__link--bloqueado"
                  aria-disabled="true"
                  data-testid={`menu-bloqueado-${item.rotulo}`}
                >
                  {item.rotulo}
                </span>
                {item.quandoAbre ? (
                  <span className="menu-lateral__nota">{item.quandoAbre}</span>
                ) : null}
              </li>
              </Fragment>
            );
          }
          const ativo = itemAtivo(item, pathname, itens);
          return (
            <Fragment key={item.para}>
            {cabecalho}
            <li
              className={classes("menu-lateral__item", item.subitem && "menu-lateral__item--sub")}
            >
              <Link
                to={item.para}
                className={classes("menu-lateral__link", ativo && "menu-lateral__link--ativo")}
                {...(ativo ? { "aria-current": "page" as const } : {})}
              >
                {item.rotulo}
              </Link>
            </li>
            </Fragment>
          );
        })}
      </ul>
    </nav>
  );
}
